import { type FC, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
const { EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const { INTERACTION_TYPE_PDP } = interactionTypeConstants;
const { STEP_INTERACTION_VIEW_INVOICE_CLICK } = stepInteractionConstants;
import { isEmailOnly } from 'dibs-cookie-jar/src/helpers/emailOnlyHelpers.ts';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import { getInvoiceHref } from '../../helpers/getInvoiceHref.ts';

import { type PdpSharedDetailItemInvoice_item$key } from './__generated__/PdpSharedDetailItemInvoice_item.graphql.ts';
import {
    type PdpSharedDetailItemInvoice_user$key,
    type PdpSharedDetailItemInvoice_user$data,
} from './__generated__/PdpSharedDetailItemInvoice_user.graphql.ts';

type ClickProps = {
    event: MouseEvent;
    user: PdpSharedDetailItemInvoice_user$data | null | undefined;
    invoiceLink: string;
};
const onInvoiceClick = async ({ event, user, invoiceLink }: ClickProps): Promise<void> => {
    event.preventDefault();

    const showAuthModal = async ({
        action,
        flow,
        email,
    }: {
        action: string;
        flow: string;
        email?: string | null;
    }): Promise<void> => {
        try {
            const { userId } =
                (await authModalLoader.show({
                    action,
                    flow,
                    email,
                    ga: {
                        label: 'pdp details invoice link',
                    },
                })) || {};

            if (userId) {
                window.location.href = handleLocaleUrl(invoiceLink, GLOBAL_CLIENT_ONLY_LOCALE);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    trackEvent({
        category: 'product',
        action: 'view invoice',
        label: 'pdp description',
        eventName: EVENT_PRODUCT_INTERACTION,
        interaction_type: INTERACTION_TYPE_PDP,
        step_interaction_name: STEP_INTERACTION_VIEW_INVOICE_CLICK,
    });

    const emailOnly = isEmailOnly(document.cookie);

    if (!user) {
        await showAuthModal({
            action: authModalLoader.constants.LOGIN,
            flow: authModalLoader.constants.INVOICE_LINK_FLOW,
        });
    } else if (emailOnly) {
        const email = user?.profile?.email;
        await showAuthModal({
            action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
            flow: authModalLoader.constants.CREATE_PASSWORD_INVOICE_FLOW,
            email,
        });
    } else {
        window.location.href = handleLocaleUrl(invoiceLink, GLOBAL_CLIENT_ONLY_LOCALE);
    }
};

type Props = {
    item: PdpSharedDetailItemInvoice_item$key;
    user: PdpSharedDetailItemInvoice_user$key | null | undefined;
    skuId?: string | null;
};
export const PdpSharedDetailItemInvoice: FC<Props> = ({ item: itemRef, skuId, user: userRef }) => {
    const item = useFragment(
        graphql`
            fragment PdpSharedDetailItemInvoice_item on Item {
                serviceId
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment PdpSharedDetailItemInvoice_user on User {
                serviceId
                profile {
                    email
                }
            }
        `,
        userRef
    );

    const itemId = item?.serviceId;
    const userId = user?.serviceId;
    const invoiceLink = getInvoiceHref(itemId, skuId);

    if (!userId) {
        return null;
    }

    return (
        <div>
            <SeoLink
                linkData={{
                    path: invoiceLink,
                    isLinkable: false,
                }}
                target="_blank"
                dataTn="pdp-spec-item-invoice"
                onClick={event =>
                    onInvoiceClick({
                        event,
                        user,
                        invoiceLink,
                    })
                }
            >
                <FormattedMessage id="pdpDetails.invoiceLink" defaultMessage="View Invoice" />
            </SeoLink>
        </div>
    );
};
